/**
 * MetricCard.js
 *
 * RATIONALE — LOADING PROP & SKELETON
 * ─────────────────────────────────────────────────────────────────
 * The component now accepts a `loading` prop. When true it renders a
 * <MetricCardSkeleton> that matches the card's dimensions exactly, so the
 * layout does not shift once real data arrives. The skeleton uses MUI's
 * <Skeleton> with wave animation, giving the user immediate visual feedback
 * that data is on its way.
 *
 * RATIONALE — SKELETON MATCHES CARD STRUCTURE
 * ─────────────────────────────────────────────────────────────────
 * The skeleton replicates the two-column grid (icon area + text area) of the
 * real card so the transition from loading → loaded feels seamless rather than
 * jarring.
 *
 * RATIONALE — onClick MADE OPTIONAL
 * ─────────────────────────────────────────────────────────────────
 * Scroll-ref callbacks have been removed from the data layer (see metricCards.js).
 * onClick is kept as an optional prop so the parent component can still wire up
 * navigation behavior without this component needing to know about it.
 */

import { useEffect, useRef, useState } from "react"
import { useTheme }    from '@mui/material/styles'
import Box             from "@mui/material/Box"
import Card            from "@mui/material/Card"
import CardContent     from "@mui/material/CardContent"
import Grid            from "@mui/material/Grid2"
import Skeleton        from "@mui/material/Skeleton"
import Typography      from "@mui/material/Typography"
import { Player }      from "@lordicon/react"


// ─── MetricCardSkeleton ───────────────────────────────────────────────────────
// Mirrors the DOM structure of MetricCard so height/width stay stable during load.

const MetricCardSkeleton = () => (
    <Card
        sx={{
            padding:  2,
            marginY:  1,
            position: 'relative',
            overflow: 'hidden',
        }}
        variant="outlined"
    >
        <CardContent>
            <Grid container rowSpacing={0} columnSpacing={2}>

                {/* Icon placeholder */}
                <Grid
                    size={{ xs: 4 }}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <Skeleton variant="circular" width={80} height={80} />
                </Grid>

                {/* Text placeholders — widths are staggered to look natural */}
                <Grid size={{ xs: 8 }} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
                    <Skeleton variant="text" width="50%" height={22} />   {/* title */}
                    <Skeleton variant="text" width="75%" height={34} />   {/* metric */}
                    <Skeleton variant="text" width="90%" height={18} />   {/* subtitle */}
                </Grid>

            </Grid>
        </CardContent>
    </Card>
)


// ─── MetricCard ───────────────────────────────────────────────────────────────

const MetricCard = ({ title, subtitle, metric, icon_obj, alert, onClick, loading }) => {

    const theme  = useTheme()
    const player = useRef(null)
    const [isActive, setIsActive] = useState(false)

    // Show skeleton while data is loading
    if (loading) return <MetricCardSkeleton />

    return (
        <Card
            onMouseEnter={() => setIsActive(true)}
            onMouseLeave={() => setIsActive(false)}
            sx={{
                transform:  isActive ? 'scale(1.04)' : 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
                padding:    2,
                marginY:    1,
                position:   'relative',
                overflow:   'hidden',
                cursor:     onClick ? 'pointer' : 'default',
                ":before": {
                    content:         '""',
                    position:        'absolute',
                    top:             0,
                    left:            0,
                    bottom:          0,
                    width:           10,
                    backgroundColor: alert
                        ? theme.palette.error.light
                        : theme.palette.background.paper,
                    borderRadius: '4px 0 0 4px',
                },
            }}
            variant="outlined"
            onClick={onClick}
        >
            <CardContent>

                <Grid container rowSpacing={0} columnSpacing={2}>

                    <Grid
                        size={{ xs: 4 }}
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        <PlayerWithHover player={player} isActive={isActive} icon_obj={icon_obj} />
                    </Grid>

                    <Grid size={{ xs: 8 }}>
                        <Typography component="p" sx={{ fontSize: '16px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Typography component="p" sx={{ fontSize: '24px', fontWeight: 300, color: 'text.secondary' }}>
                            {metric}
                        </Typography>
                        <Typography component="p" sx={{ fontSize: '12px' }}>
                            {subtitle}
                        </Typography>
                    </Grid>

                </Grid>

            </CardContent>
        </Card>
    )
}


// ─── PlayerWithHover ──────────────────────────────────────────────────────────
// Extracted into its own component so the useEffect has a stable, focused scope.

const PlayerWithHover = ({ player, isActive, icon_obj }) => {
    useEffect(() => {
        if (isActive) {
            player?.current?.playFromBeginning()
        } else {
            player?.current?.pause()
            player?.current?.goToFirstFrame()
        }
    }, [isActive, player])

    return (
        <Player
            ref={player}
            size={80}
            icon={icon_obj}
            direction={1}
        />
    )
}


export default MetricCard